import React, { useState } from 'react';
import { FiUser, FiMail, FiPhone, FiLoader } from 'react-icons/fi'; // Thêm icon loading
import styles from './ContactForm.module.css';
import Input from "../fields/Input/Input";
import DescriptionTextarea from "../fields/Textarea/DescriptionTextarea";
import { sendEmail } from "../../services/contactService";
import Alert from "../Alert/Alert";

const ContactForm = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alertStatus, setAlertStatus] = useState({ show: false, type: '', message: '' });

    const validate = () => {
        let newErrors = {};
        if (!formData.fullName.trim()) newErrors.fullName = "Bitte geben Sie Ihren Namen ein.";

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email) {
            newErrors.email = "Bitte geben Sie Ihre E-Mail-Adresse ein.";
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = "Ungültige E-Mail-Adresse.";
        }

        const phoneRegex = /^[0-9+\s]{9,15}$/;
        if (!formData.phone) {
            newErrors.phone = "Bitte geben Sie Ihre Telefonnummer ein.";
        } else if (!phoneRegex.test(formData.phone)) {
            newErrors.phone = "Ungültige Telefonnummer.";
        }

        if (!formData.message || formData.message.length < 4) {
            newErrors.message = "Ihre Nachricht ist zu kurz.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setAlertStatus({ show: false, type: '', message: '' });

        // Nội dung Email bằng tiếng Đức
        const emailSubject = `Neue Kontaktanfrage von ${formData.fullName}`;
        const emailHtml = `
            <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 600px; border: 1px solid #eee; padding: 20px;">
                <h2 style="color: #e63946; border-bottom: 2px solid #e63946; padding-bottom: 10px;">Neue Kontaktanfrage</h2>
                <p>Sie haben một tin nhắn mới từ trang web:</p>
                <table style="width: 100%; border-collapse: collapse;">
                    <tr>
                        <td style="padding: 8px; font-weight: bold; width: 30%;">Name:</td>
                        <td style="padding: 8px;">${formData.fullName}</td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; font-weight: bold;">Email:</td>
                        <td style="padding: 8px;">${formData.email}</td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; font-weight: bold;">Telefon:</td>
                        <td style="padding: 8px;">${formData.phone}</td>
                    </tr>
                </table>
                <div style="margin-top: 20px; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #e63946;">
                    <strong>Nachricht:</strong><br/>
                    ${formData.message.replace(/\n/g, '<br/>')}
                </div>
                <p style="font-size: 12px; color: #777; margin-top: 30px;">Diese E-Mail wurde automatisch von Ihrem Kontaktformular gesendet.</p>
            </div>
        `;

        try {
            // Giả sử API nhận: to, subject, html
            // "recipient@example.com" là mail của chủ nhà hàng
            await sendEmail(emailSubject, emailHtml);

            setAlertStatus({
                show: true,
                type: 'success',
                message: 'Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.'
            });
            setFormData({ fullName: '', email: '', phone: '', message: '' });
        } catch (error) {
            setAlertStatus({
                show: true,
                type: 'error',
                message: 'Entschuldigung, etwas ist schief gelaufen. Bitte versuchen Sie es später erneut.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.formContainer}>
            {alertStatus.show && (
                <Alert
                    type={alertStatus.type}
                    message={alertStatus.message}
                    onClose={() => setAlertStatus({ ...alertStatus, show: false })}
                />
            )}

            <h2 className={styles.formTitle}>Kontaktieren Sie uns</h2>
            <p className={styles.formSubtitle}>Bitte hinterlassen Sie Ihre Daten, das Restaurant wird sich mit Ihnen in Verbindung setzen.</p>

            <form className={styles.gridForm} onSubmit={handleSubmit} noValidate>
                <div className={styles.fieldWrapper}>
                    <Input
                        icon={FiUser}
                        placeholder="Vollständiger Name"
                        value={formData.fullName}
                        onChange={(e) => handleChange('fullName', e.target.value)}
                        error={errors.fullName}
                        disabled={loading}
                    />
                    {errors.fullName && <span className={styles.errorText}>{errors.fullName}</span>}
                </div>

                <div className={styles.fieldWrapper}>
                    <Input
                        icon={FiMail}
                        placeholder="E-Mail-Adresse"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        error={errors.email}
                        disabled={loading}
                    />
                    {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                </div>

                <div className={styles.fieldWrapper}>
                    <Input
                        icon={FiPhone}
                        placeholder="Telefonnummer"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        error={errors.phone}
                        disabled={loading}
                    />
                    {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>

                <div className={styles.fieldWrapper}>
                    <DescriptionTextarea
                        placeholder="Ihre Nachricht..."
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        rows={5}
                        error={errors.message}
                        disabled={loading}
                    />
                    {errors.message && <span className={styles.errorText}>{errors.message}</span>}
                </div>

                <button type="submit" className={styles.submitBtn} disabled={loading}>
                    {loading ? (
                        <>
                            <FiLoader className={styles.spinner} /> Senden...
                        </>
                    ) : (
                        "Nachricht senden"
                    )}
                </button>
            </form>
        </div>
    );
};

export default ContactForm;